import type Luminary from '@lumen5/luminary';
import type { Asset, Scene, SceneBoundary, TimeAwareMediaAsset } from '@lumen5/luminary';
import { getMorphableAssets, VideoData } from '@lumen5/luminary';

import { WATERMARK_ASSET_NAME } from 'js/src/libs/consts';
import {
    getPrePackagedMorphPosition,
    hasAppliedMatchCut,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';

export type MorphChainMember = {
    sceneId: string;
    assetId: string;
    duration: number;
};

export function getOrderedMorphChain(
    luminary: Readonly<Luminary>,
    focusedSceneId: string,
    focusedAssetId: string,
): MorphChainMember[] {
    const focusedScene = luminary.getSceneById(focusedSceneId);
    const focusedAsset = focusedScene?.getAssetById(focusedAssetId);
    if (!focusedScene || !focusedAsset) {
        return [];
    }

    const toMember = (scene: Scene, asset: Asset): MorphChainMember => ({
        sceneId: scene.id,
        assetId: asset.id,
        duration: scene.duration,
    });

    const isLinkedMatch = (asset: Asset) =>
        !asset.isMatchCutDuplicate && asset.isMorphMatch(focusedAsset);

    const before: MorphChainMember[] = [];
    let current = focusedScene;
    while (current.prevScene?.morphEnabled) {
        const prevScene = current.prevScene;
        const match = prevScene.getAssets().find(isLinkedMatch);
        if (!match) {
            break;
        }
        before.unshift(toMember(prevScene, match));
        current = prevScene;
    }

    const after: MorphChainMember[] = [];
    current = focusedScene;
    while (current.morphEnabled && current.nextScene) {
        const nextScene = current.nextScene;
        const match = nextScene.getAssets().find(isLinkedMatch);
        if (!match) {
            break;
        }
        after.push(toMember(nextScene, match));
        current = nextScene;
    }

    return [...before, toMember(focusedScene, focusedAsset), ...after];
}

/**
 * Walks the scene sequence from the focused asset and returns a map of sceneId → assetId
 * for all linked assets. Includes the focused asset itself only if the chain has
 * at least one other member. Each asset morphs to exactly 1 asset in an adjacent scene.
 */
export function getSceneSequenceMap(
    luminary: Readonly<Luminary>,
    focusedSceneId: string,
    focusedAssetId: string,
): Map<string, string> {
    const chain = getOrderedMorphChain(luminary, focusedSceneId, focusedAssetId);
    if (chain.length <= 1) {
        return new Map();
    }
    return new Map(chain.map(({ sceneId, assetId }) => [sceneId, assetId]));
}

export function getMorphChainHeadAsset(
    luminary: Readonly<Luminary>,
    chain: readonly MorphChainMember[],
): TimeAwareMediaAsset | undefined {
    const head = chain[0];
    if (!head) {
        return undefined;
    }
    const asset = luminary.getSceneById(head.sceneId)?.getAssetById(head.assetId);
    return asset && VideoData.isMediaAsset(asset) ? (asset as TimeAwareMediaAsset) : undefined;
}

export function isChainTrimmable(
    luminary: Readonly<Luminary>,
    chain: readonly MorphChainMember[],
): boolean {
    if (chain.length <= 1) {
        return false;
    }

    return chain.every(({ sceneId, assetId }) => {
        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAssetById(assetId);
        if (!scene || !asset || !VideoData.isMediaAsset(asset)) {
            return false;
        }

        const sceneGroup = scene.sceneGroup;
        if (!sceneGroup || isVoiceDrivenSceneGroup(sceneGroup)) {
            return false;
        }
        const mediaAsset = asset as TimeAwareMediaAsset;
        if (mediaAsset.linkToSceneGroup) {
            return false;
        }

        if (scene.matchCutTechnique !== VideoData.MATCH_CUT_NONE) {
            return false;
        }

        if (scene.subscenes.length > 1) {
            return false;
        }

        return mediaAsset.playbackRegions.length === 1;
    });
}

export type AnimatedZoomGapState = {
    /** The scene that carries the morph — the one before the gap. */
    zoomSceneId: string | null;
    isEnabled: boolean;
    isAvailable: boolean;
};

// The watermark is the same asset on every scene, so counting it would read every gap in the video
// as showing the same media. It is chrome the template stamps on top, never something a zoom moves.
function getZoomableMediaAssets(scene: Scene, boundary: SceneBoundary): TimeAwareMediaAsset[] {
    return getMorphableAssets(scene, boundary)
        .filter(VideoData.isMediaAsset)
        .filter((asset) => asset.name !== WATERMARK_ASSET_NAME);
}

/** Covers both a scene's own media and a window onto the scene group's base media. */
export function mediaAssetsMatchAcrossGap(fromScene: Scene, intoScene: Scene): boolean {
    const sources = getZoomableMediaAssets(fromScene, 'end');
    const targets = getZoomableMediaAssets(intoScene, 'start');
    return sources.some((source) => targets.some((target) => source.isMorphMatch(target)));
}

/**
 * Whether the gap into `intoScene` can be animated as a zoom/pan: the two scenes have to show the
 * same media, so morphing between them reads as a camera move within one image rather than a cut.
 *
 * Unlike the text match-cut recommendation this deliberately tolerates morphing neighbours, so
 * several zooms can be chained across consecutive scenes.
 */
export function getAnimatedZoomGapState(intoScene: Scene): AnimatedZoomGapState {
    const fromScene = intoScene.prevScene;
    if (!fromScene) {
        return { zoomSceneId: null, isEnabled: false, isAvailable: false };
    }

    const isPartOfSceneSequence =
        getPrePackagedMorphPosition(fromScene) !== null ||
        getPrePackagedMorphPosition(intoScene) !== null;
    const neighboursMatchCut = [fromScene.prevScene, intoScene.nextScene].some(
        (neighbour) => !!neighbour && hasAppliedMatchCut(neighbour),
    );

    return {
        zoomSceneId: fromScene.id,
        isEnabled: fromScene.morphEnabled,
        isAvailable:
            !isPartOfSceneSequence &&
            !neighboursMatchCut &&
            !hasAppliedMatchCut(fromScene) &&
            mediaAssetsMatchAcrossGap(fromScene, intoScene),
    };
}
